/**
 * PinDetailPanel
 *
 * Appears when the user clicks an individual Impact Pin.
 * Shows the full story for that event + a "Support this Movement" CTA.
 */
import { useImpactEventById } from '@/hooks/useImpactEvents';
import { useMapStore } from '@/stores/mapStore';
import { format } from 'date-fns';

export default function PinDetailPanel() {
  const selectedFeature = useMapStore((s) => s.selectedFeature);
  const clearSelectedFeature = useMapStore((s) => s.clearSelectedFeature);
  const openGiveModal = useMapStore((s) => s.openGiveModal);

  const eventId = selectedFeature?.properties?.uuid;
  const { data: event, isLoading } = useImpactEventById(eventId);

  if (!selectedFeature) return null;

  const props = selectedFeature.properties || {};

  return (
    <div className="fixed bottom-32 left-4 z-40 w-80 animate-slide-in-right rounded-2xl border border-neutral-200 bg-paper shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#003cc5]">Impact Event</span>
        <button
          onClick={clearSelectedFeature}
          className="rounded p-1 text-neutral-500 hover:text-ink"
          aria-label="Close"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="px-4 py-4">
        {isLoading ? (
          <div className="space-y-2 animate-skeleton">
            <div className="h-5 w-3/4 rounded bg-neutral-200" />
            <div className="h-3 w-full rounded bg-neutral-200/70" />
            <div className="h-3 w-2/3 rounded bg-neutral-200/70" />
          </div>
        ) : (
          <>
            {(event?.properties?.region || props.region) && (
              <p className="mb-2 flex items-center gap-1 text-xs text-neutral-500">
                <svg className="h-3 w-3 text-[#003cc5]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <circle cx="12" cy="11" r="3" />
                </svg>
                {event?.properties?.region || props.region}
              </p>
            )}

            {(event?.properties?.impact_count ?? props.impact_count) > 0 && (
              <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-[#003cc5] border border-red-200">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {(event?.properties?.impact_count ?? props.impact_count).toLocaleString()} people impacted
              </div>
            )}

            {event?.properties?.narrative && (
              <p className="mb-3 text-sm leading-relaxed text-neutral-600">{event.properties.narrative}</p>
            )}

            {(event?.properties?.event_date || props.event_date) && (
              <p className="mb-4 text-xs text-neutral-500">
                {format(new Date(event?.properties?.event_date || props.event_date), 'MMMM d, yyyy')}
              </p>
            )}
          </>
        )}

        <button
          onClick={() =>
            openGiveModal({
              storyId: eventId,
              regionName: event?.properties?.region || props.region,
            })
          }
          className="w-full rounded-xl bg-[#003cc5] py-2.5 text-sm font-semibold text-parchment transition hover:bg-[#0036b1] active:scale-95"
        >
          Give Now — Support this Movement
        </button>
      </div>
    </div>
  );
}
